import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserRoleService } from '@shared/services/role.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { UpdateBatchMutation } from '../../../graphql/mutations/update-batch';
import { IBatch, IBatchStatus, MesBatchService } from '../mes-batch-service';

@Component({
	selector: 'app-edit-batch',
	templateUrl: './edit-batch.component.html',
	styleUrl: './edit-batch.component.scss',
	providers: [UpdateBatchMutation],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditBatchComponent implements OnInit {
	@Output() edited: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	loading: boolean = false;
	hasChanges: boolean = false;
	batch: IBatch;
	httpError = { message: '', statusCode: 0 };

	statusOrder: IBatchStatus[] = [IBatchStatus.Draft, IBatchStatus.Failed, IBatchStatus.Prepare, IBatchStatus.Ready, IBatchStatus.Production, IBatchStatus.Done, IBatchStatus.Archived];

	selectedStatus: IBatchStatus;

	constructor(
		@Inject(MAT_DIALOG_DATA) public editBatch: IBatch,
		public dialogRef: MatDialogRef<EditBatchComponent>,
		private userRoleService: UserRoleService,
		private fb: FormBuilder,
		private service: MesBatchService,
	) {
		this.batch = { ...editBatch };
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.batch.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.batch.name, [Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			status: this.batch.status,
		});
		this.form.valueChanges.subscribe((value) => {
			const aliasChanged = value.alias !== this.batch.alias;
			const nameChanged = value.name !== this.batch.name;

			this.hasChanges = aliasChanged || nameChanged;

			Object.keys(value).forEach((key) => {
				if (key in this.batch) {
					this.batch[key] = value[key];
				}
			});
		});
	}

	private updateStatus(newStatus: IBatchStatus | null) {
		this.hasChanges = newStatus !== null && this.selectedStatus !== newStatus;
		this.selectedStatus = newStatus;
	}

	onDraftChange() {
		this.updateStatus(this.selectedStatus === IBatchStatus.Draft ? null : IBatchStatus.Draft);
	}

	onNextChange() {
		const nextStatus = this.nextStatusInLine();
		this.updateStatus(this.selectedStatus === nextStatus ? null : nextStatus);
	}

	nextStatusInLine(): IBatchStatus {
		const currentIndex = this.statusOrder.indexOf(this.batch.status);

		// Find the next valid status (skip 'Failed')
		for (let i = currentIndex + 1; i < this.statusOrder.length; i++) {
			if (this.statusOrder[i] !== IBatchStatus.Failed) {
				return this.statusOrder[i];
			}
		}

		return this.batch.status;
	}

	async onSave() {
		if (this.loading) return;
		this.httpError = { message: '', statusCode: undefined };
		if (this.form.valid) {
			this.loading = true;
			this.batch.status = this.selectedStatus || this.batch.status;
			this.service.update(this.batch).subscribe({
				next: () => {
					this.edited.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = {
						message: errorResponse.error?.error?.message || 'An unexpected error occurred',
						statusCode: errorResponse.status,
					};
					this.loading = false;
				},
			});
		}
	}

	onCancel = () => this.dialogRef.close();

	get controls() {
		return this.form.controls;
	}

	get hideButton(): boolean {
		const hideButtonStatuses = new Set([IBatchStatus.Done, IBatchStatus.Archived]);

		return hideButtonStatuses.has(this.batch.status);
	}

	get isArchived(): boolean {
		return this.batch.status === 'Archived';
	}

	get fieldIsReadonly(): boolean {
		const readOnlyStatuses = new Set([IBatchStatus.Failed, IBatchStatus.Prepare, IBatchStatus.Ready, IBatchStatus.Production, IBatchStatus.Done, IBatchStatus.Archived]);

		return readOnlyStatuses.has(this.batch.status);
	}

	get disableDraftButton(): boolean {
		const disableDraftStatuses = new Set([IBatchStatus.Draft, IBatchStatus.Prepare, IBatchStatus.Production]);

		return disableDraftStatuses.has(this.batch.status);
	}

	get disableNextButton(): boolean {
		const disableNextStatuses = new Set([IBatchStatus.Prepare, IBatchStatus.Failed]);

		return disableNextStatuses.has(this.batch.status);
	}

	get isDraftSelected(): boolean {
		return this.selectedStatus === IBatchStatus.Draft;
	}

	get isNextSelected(): boolean {
		return this.selectedStatus === this.nextStatusInLine();
	}

	get isMesManager(): boolean {
		return this.userRoleService.userHasRole('MesManager');
	}
}
